import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type LeadCreateNodeType } from 'automations/types/nodeTypes';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import { useCallback, useState } from 'react';
import { ZodError } from 'zod';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ShowConditionsUi from '../ShowConditionsUi';
import styles from './LeadCreateMenu.module.scss';

export default function LeadCreateMenu({
  node,
}: {
  node: NodeProps<LeadCreateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const [isLoading, setIsLoading] = useState(false);

  const [savedCriteriaIds, setSavedCriteriaIds] = useState<Record<'additional' | 'removal', number | undefined>>({
    additional: node.data.additionalOptions?.criteriaGroupId,
    removal: node.data.additionalOptions?.removableCriteriaGroupId,
  });

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: {
          criteriaGroupId: savedCriteriaIds.additional,
          removableCriteriaGroupId: savedCriteriaIds.removal,
        },
      }));
      await dispatch(saveTriggerNode(node.id, automationId, contentMode));
      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, node.id, savedCriteriaIds.additional, savedCriteriaIds.removal,
    automationId, contentMode, setMenuOpen]);

  const handleAdditionalConditionsChange = useCallback((criteriaId?: number) => {
    setSavedCriteriaIds((prev) => ({ ...prev, additional: criteriaId }));
  }, []);

  const handleRemovalConditionsChange = useCallback((criteriaId?: number) => {
    setSavedCriteriaIds((prev) => ({ ...prev, removal: criteriaId }));
  }, []);

  const AdditionalConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Additional Conditions"
    />
  ), []);

  const RemovalConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Removal Conditions"
    />
  ), []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When #{${TranslationEntity.lead}} is added`,
          [{ capitalize: true }, { capitalize: true, withArticle: true }],
        ),
      }}
    >
      <Card title={contentMode === 'edit' ? 'Set Trigger' : 'Edit Trigger'} className={styles.containerCard}>
        <Flex vertical gap={16}>
          <Row align="middle">
            <Col span={6}>
              Set Additional Conditions
            </Col>
            <Col span={18}>
              <AutomationsCriteriaConditionsWrapper
                criteriaId={savedCriteriaIds.additional}
                onCriteriaChange={handleAdditionalConditionsChange}
                builderUi={AdditionalConditionsBuilderUi}
              />
            </Col>
          </Row>

          <Divider dashed />

          <Row align="middle">
            <Col span={6}>
              Set Removal Conditions
            </Col>
            <Col span={18}>
              <AutomationsCriteriaConditionsWrapper
                criteriaId={savedCriteriaIds.removal}
                onCriteriaChange={handleRemovalConditionsChange}
                builderUi={RemovalConditionsBuilderUi}
              />
            </Col>
          </Row>
        </Flex>
      </Card>

      <Flex justify="flex-end" gap={16} className={styles.actionButton}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
